import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../theme/utils';
import Flex from '../../../../components/Flex';
import { PrioTheme } from '../../../../theme/types';
import { useTheme } from 'react-jss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrioSpinner, PillCRA as Pill } from '@prio365/prio365-react-library';
import { useSelector } from 'react-redux';
import { getUserMe } from '../../../../apps/main/rootReducer';
import { OfficeId } from '../../../../models/Types';
import { apiFetchSearch } from '../../api';
import { useNavigate } from 'react-router-dom';
import useOfficesContext from '../../../companies/hooks/useOfficesContext';
import moment from 'moment';
import useContactsContext from '../../../contacts/hooks/useContactsProvider';
import { useQuery } from '@tanstack/react-query';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  header: {
    fontSize: theme.font.fontSize.large,
    fontWeight: theme.font.fontWeight.bold,
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${theme.spacing.small}px 0px`,
    paddingRight: theme.spacing.small,
    borderBottom: `1px solid ${theme.colors.application.border}`,
    backgroundColor: theme.colors.application.background.default,
    '&:hover': {
      backgroundColor: theme.colors.base.primary,
    },
  },
  actionContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.regular,
    flex: 1,
  },
  actionTitle: {
    fontSize: theme.font.fontSize.regular,
    fontWeight: theme.font.fontWeight.regular,
    color: theme.colors.application.typography.default,
  },
  actionType: {
    fontSize: theme.font.fontSize.tiny,
    color: theme.colors.application.typography.muted,
    marginTop: theme.spacing.extraSmall,
  },
  pillContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
    cursor: 'pointer',
  },
  centerInRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.small,
    flex: 1,
    overflow: 'scroll',
  },
  iconNumber: {
    color: theme.colors.base.primary.default,
    fontSize: theme.font.fontSize.regular,
    fontWeight: theme.font.fontWeight.bold,
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  overflowHidden: {
    overflow: 'hidden',
  },
  officeLabel: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.default,
    marginRight: theme.spacing.regular,
  },
  pointer: {
    cursor: 'pointer',
  },
}));

type TimekeepingOverviewItemType =
  | 'overtimeHours'
  | 'deficitHours'
  | 'totalOvertimeHours'
  | 'monthlyClosesNotClosed';

interface PendingAction {
  icon: ReactNode;
  numberOfActions: number;
  type: TimekeepingOverviewItemType;
  linkToAction: string;
  actionLabel: string;
  iconLabel: string;
}

interface DashboardHRTimekeepingItemProps {
  className?: string;
  height?: number;
}

const generateSearchString = (
  type: TimekeepingOverviewItemType,
  officeIdsWhereOfficeHR: OfficeId[]
) => {
  const parsedOfficeIds = officeIdsWhereOfficeHR
    .map((officeId) => `'${officeId}'`)
    .join(',')
    .toString();

  const officeIdValue =
    parsedOfficeIds === '' ? '' : `OfficeId eq ${parsedOfficeIds}`;

  let stateValue = '';
  let monthFromValue = `Month ge '${moment()
    .startOf('month')
    .subtract(1, 'day')
    .format('YYYY-MM-DD')}T23:00:00Z'`;
  let monthToValue = `Month le '${moment()
    .endOf('month')
    .format('YYYY-MM-DD')}T21:59:59Z'`;

  if (type === 'deficitHours') {
    stateValue = `AccumulatedOvertimeHours lt '-20'`;
  }

  if (type === 'overtimeHours') {
    stateValue = `AccumulatedOvertimeHours gt '50'`;
  }

  if (type === 'monthlyClosesNotClosed') {
    stateValue = `EmployeeConfirmation eq 'false'`;
    monthFromValue = '';
    monthToValue = `Month le '${moment()
      .subtract(1, 'month')
      .endOf('month')
      .format('YYYY-MM-DD')}T21:59:59Z'`;
  }

  return `
  ${officeIdValue} 
  ${stateValue ? `%26 ${stateValue}` : ''}
  ${monthFromValue ? `%26 ${monthFromValue}` : ''}
  ${
    monthToValue ? `%26 ${monthToValue}` : ''
  }%26 Calculated.EmployeeIsArchived eq 'false'
  `;
};

const generateLinkToActions = (
  type: TimekeepingOverviewItemType,
  userOfficeId: OfficeId,
  officeIdsWhereOfficeHR: OfficeId[]
) => {
  const officeId = officeIdsWhereOfficeHR.find(
    (officeId) => officeId === userOfficeId
  )
    ? userOfficeId
    : officeIdsWhereOfficeHR[0];

  const path = `/module/prio/hr/office/${officeId}/timekeeping/monthlyClose`;
  let query = `?s=${generateSearchString(type, officeIdsWhereOfficeHR)}`;
  let pickedFilters = '';

  if (type === 'overtimeHours' || type === 'deficitHours') {
    pickedFilters = `&pickedFilters=Data.AccumulatedOvertimeHours`;
  }

  return `${path}${query}${pickedFilters}`;
};

const useHRDetails = (
  searchType,
  searchString: string,
  useSum,
  shortLoading
) => {
  const threshold = shortLoading ? 10 : 1000;
  const forceResults = shortLoading ? false : true;
  const { data, isLoading } = useQuery({
    queryKey: [searchType, searchString, threshold, forceResults],
    queryFn: () =>
      apiFetchSearch(
        searchType ?? '',
        searchString ?? '',
        threshold,
        forceResults
      ),
    staleTime: 1000 * 60 * 60 * 20, // 20 hours
  });
  const numberOfEntities = data?.data?.totalItems;
  const totalOvertimeHours = data?.data?.items?.reduce(
    (acc: number, item: any) => {
      return acc + item?.data['accumulatedOvertimeHours'];
    },
    0
  );
  const _totalOvertimeHoursRounded = Math.floor(totalOvertimeHours as number);
  const totalOvertimeHoursRounded: number | undefined = isLoading
    ? undefined
    : _totalOvertimeHoursRounded;

  const value = useSum ? totalOvertimeHoursRounded : numberOfEntities;

  return { value, isLoading };
};

export const DashboardHRTimekeepingItem: React.FC<
  DashboardHRTimekeepingItemProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { className } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const navigate = useNavigate();

  const { getOfficeById } = useOfficesContext();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector(getUserMe);

  const employeeId = useMemo(() => {
    return userMe?.id;
  }, [userMe?.id]);

  const { getContactById } = useContactsContext();
  const officeId = getContactById(employeeId)?.officeId;

  const officeIdsWhereOfficeHR: OfficeId[] = useMemo(
    () =>
      Object.entries(userMe?.prioData?.officeRoles)
        .filter(([_, roles]) => {
          return roles.includes('officeHR');
        })
        .map(([officeId, _]) => officeId),
    [userMe?.prioData?.officeRoles]
  );

  const offices: OfficeId[] = useMemo(
    () =>
      officeIdsWhereOfficeHR.map((officeId) => getOfficeById(officeId)?.name),
    [officeIdsWhereOfficeHR, getOfficeById]
  );

  const {
    value: numberOfPeopleWithOvertimeHours,
    isLoading: isOvertimeLoading,
  } = useHRDetails(
    'monthlyCloses',
    generateSearchString('overtimeHours', officeIdsWhereOfficeHR),
    false,
    true
  );

  const { value: numberOfPeopleWithDeficitHours, isLoading: isDeficitLoading } =
    useHRDetails(
      'monthlyCloses',
      generateSearchString('deficitHours', officeIdsWhereOfficeHR),
      false,
      true
    );

  const { value: totalOvertimeHours, isLoading: isTotalLoading } = useHRDetails(
    'monthlyCloses',
    generateSearchString('totalOvertimeHours', officeIdsWhereOfficeHR),
    true,
    false
  );

  const { value: numberOfMonthlyClosesNotClosed, isLoading: isClosesLoading } =
    useHRDetails(
      'monthlyCloses',
      generateSearchString('monthlyClosesNotClosed', officeIdsWhereOfficeHR),
      false,
      true
    );

  const isLoading =
    isOvertimeLoading || isDeficitLoading || isTotalLoading || isClosesLoading;

  const pendingActions: PendingAction[] = useMemo(
    () =>
      [
        {
          actionLabel: t(`dashboard:hrTimeKeepingOverview.showDetails`),
          iconLabel: t(`dashboard:hrTimeKeepingOverview.employees`),
          icon: null,
          numberOfActions: numberOfPeopleWithDeficitHours,
          type: 'deficitHours',
          linkToAction: generateLinkToActions(
            'deficitHours',
            officeId,
            officeIdsWhereOfficeHR
          ),
        } as PendingAction,
        {
          actionLabel: t(`dashboard:hrTimeKeepingOverview.showDetails`),
          iconLabel: t(`dashboard:hrTimeKeepingOverview.employees`),
          icon: null,
          numberOfActions: numberOfPeopleWithOvertimeHours,
          type: 'overtimeHours',
          linkToAction: generateLinkToActions(
            'overtimeHours',
            officeId,
            officeIdsWhereOfficeHR
          ),
        } as PendingAction,
        {
          actionLabel: t(`dashboard:hrTimeKeepingOverview.showDetails`),
          iconLabel: t(`dashboard:hrTimeKeepingOverview.totalOvertimeHours`),
          icon: (
            <FontAwesomeIcon
              icon={['fal', 'business-time']}
              color={theme.colors.application.typography.default}
            />
          ),
          numberOfActions: totalOvertimeHours,
          type: 'totalOvertimeHours',
          linkToAction: generateLinkToActions(
            'totalOvertimeHours',
            officeId,
            officeIdsWhereOfficeHR
          ),
        } as PendingAction,
        {
          actionLabel: t(`dashboard:hrTimeKeepingOverview.showDetails`),
          iconLabel: t(
            `dashboard:hrTimeKeepingOverview.monthlyClosesNotClosed`
          ),
          icon: (
            <FontAwesomeIcon
              icon={['fal', 'calendar-clock']}
              color={theme.colors.application.typography.default}
            />
          ),
          numberOfActions: numberOfMonthlyClosesNotClosed,
          type: 'monthlyClosesNotClosed',
          linkToAction: generateLinkToActions(
            'monthlyClosesNotClosed',
            officeId,
            officeIdsWhereOfficeHR
          ),
        } as PendingAction,
      ].filter((action) => action.numberOfActions > 0),
    [
      t,
      theme,
      numberOfMonthlyClosesNotClosed,
      totalOvertimeHours,
      numberOfPeopleWithDeficitHours,
      numberOfPeopleWithOvertimeHours,
      officeIdsWhereOfficeHR,
      officeId,
    ]
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleActionClick = (action: PendingAction) => {
    navigate(action.linkToAction);
  };

  const renderPill = (number: number, icon: ReactNode) => {
    return (
      <Pill style={{ width: 'fit-content' }}>
        <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
          {icon}
          <span style={{ color: theme.colors.application.typography.default }}>
            {number ?? <PrioSpinner size="small" />}
          </span>
        </Flex.Row>
      </Pill>
    );
  };
  //#endregion

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      <Flex.Row
        childrenGap={theme.spacing.small}
        marginBottom={8}
        alignItems="center"
      >
        <Flex.Item flex={1}>
          <div className={classes.header}>
            {t('dashboard:hrTimeKeepingOverview.title')}
          </div>
        </Flex.Item>
        <Flex.Item className={classes.overflowHidden}>
          <div
            title={offices.join(' - ')}
            className={classNames(classes.ellipsis, classes.officeLabel)}
          >
            {offices.join(' - ')}
          </div>
        </Flex.Item>
      </Flex.Row>

      {isLoading && (
        <div className={classes.centerInRow}>
          <PrioSpinner size="large" />
        </div>
      )}

      {!isLoading && pendingActions.length === 0 && (
        <div className={classes.centerInRow}>
          <div
            style={{
              color: theme.colors.application.typography.muted,
              fontSize: theme.font.fontSize.small,
            }}
          >
            {t('dashboard:hrTimeKeepingOverview.noActions')}
          </div>
        </div>
      )}

      {!isLoading && pendingActions.length > 0 && (
        <div className={classes.actionsList}>
          {pendingActions.map((action, index) => (
            <div key={index} className={classes.actionRow}>
              <div className={classes.actionContent}>
                <div>
                  <div className={classes.actionTitle}>
                    {`${t(
                      `dashboard:hrTimeKeepingOverview.headline.${action.type}`
                    )} ${action.type === 'overtimeHours' ? '(> 50h)' : action.type === 'deficitHours' ? '(< -20h)' : ''}`}
                  </div>
                  <div className={classes.actionType}>
                    {t(`dashboard:hrTimeKeepingOverview.desc.${action.type}`)}
                  </div>
                </div>
              </div>
              <div
                className={classes.pillContainer}
                onClick={() => handleActionClick(action)}
              >
                {renderPill(action.numberOfActions, action.icon)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Flex.Column>
  );
};

export default DashboardHRTimekeepingItem;
